import { computed, defineComponent } from "vue";
import { Container } from "aurelia-dependency-injection";
import { useVuelidate } from "@vuelidate/core";
import { mapState } from "pinia";

import config from "../../../../common/config";
import { useApp } from "../../useApp";
import { ICompanySecuritySetup } from "../../../../resources-vue/vue-interfaces/i-company-security-setup";
import { ToastrService } from "../../../../services/toastr.service";
import { OrganizationService } from "../../../../services/organization.service";

import PageLayout from "../../../../resources-vue/vue-custom-elements/PageLayout/PageLayout.vue";
import SecurityAuthentication from "./components/SecurityAuthentication/SecurityAuthentication.vue";
import SignatureVerification from "../../../../resources-vue/vue-dialogs/SignatureVerification/SignatureVerification.vue";

export default defineComponent({
    components: {
        PageLayout,
        SecurityAuthentication
    },
    data(vm) {
        return {
            _toastrService: null as ToastrService,
            _organizationService: null as OrganizationService,
            securitySetup: null as ICompanySecuritySetup,
            organization: null,
            isLoadError: false,
            isFormLoading: true,
            isSaveInProgress: false,
            isSignatureValidated: false,
            dialogRef: null,
            v$: useVuelidate()
        };
    },

    provide() {
        return {
            dialogRef: computed(() => this.dialogRef)
        };
    },

    created() {
        this._organizationService = Container.instance.get(OrganizationService);
        this._toastrService = Container.instance.get(ToastrService);
    },

    computed: {
        ...mapState(useApp, ["organizations"])
    },

    async mounted() {
        const organizationName = window.localStorage.getItem(config.organizationName);
        this.organization = this.organizations.find(
            (organization: { name: string }) => organization.name === organizationName
        );

        this.validateSignature();
        this.loadCompanySecuritySetup();
    },

    methods: {
        validateSignature() {
            this.dialogRef = this.$dialog.open(SignatureVerification, {
                props: {
                    modal: true,
                    showHeader: false,
                    style: "width: 55vw"
                },
                data: {
                    organizationName: this.organization.name
                },
                onClose: (options) => {
                    if (options.data === "success") {
                        this.isSignatureValidated = true;
                    } else {
                        this.$router.back();
                    }
                }
            });
        },

        async loadCompanySecuritySetup() {
            this.isFormLoading = true;
            this.isLoadError = false;
            try {
                const resp = await this._organizationService.getTwoFAConfiguration(this.organization.id);
                if (resp.IsSuccessful) {
                    this.securitySetup = {
                        IsEnabled: resp.IsEnabled,
                        AllowedTypes: resp.AllowedTypes,
                        OrganizationId: this.organization.id
                    };
                } else {
                    this._toastrService.error({
                        title: `Failed to Load Company Security Setup`,
                        message: resp?.Message || "Please Retry"
                    });
                }
            } catch (err) {
                this.isLoadError = true;
                this._toastrService.error({
                    title: `Failed to Load Company Security Setup`,
                    message: `Please Contact Axxess if issue persists.`
                });
            } finally {
                this.isFormLoading = false;
            }
        },

        async save() {
            const isValidated = await this.v$.$validate();
            try {
                if (isValidated) {
                    this.isSaveInProgress = true;
                    const resp = await this._organizationService.postTwoFAConfiguration(this.securitySetup);
                    if (resp.IsSuccessful) {
                        this._toastrService.success({
                            title: `Updated Company Security Setup`,
                            message: `Company Security Setup has been successfully updated`
                        });
                    } else {
                        this._toastrService.error({
                            title: `Failed to Save Company Security Setup`,
                            message:
                                resp?.Message ||
                                `There was a problem while saving Company Security Setup. Please try again. If the problem persists, please report it.`
                        });
                    }
                } else {
                    this._toastrService.error({
                        title: `Error`,
                        message: `Please complete all required fields.`
                    });
                }
            } catch (e) {
                this._toastrService.error({
                    title: `Error`,
                    message: `There was a problem while saving Company Security Setup. Please try again. If the problem persists, please report it.`
                });
            } finally {
                this.isSaveInProgress = false;
            }
        },

        cancel() {
            this.$router.back();
        }
    }
});
